import { useCallback, useEffect, useState } from 'react';
import { isFullSongVideoTheme, type CreateAssetPublishBody } from 'fym-shared/src/music24/oneOffPublish';
import { useCreateAssetPublishMutation } from '../../../../redux/api/fymAPI';
import { addPublishWatches } from './publishDialogStore';
import { useArtistTimeZone } from '../../../settings/tabs/artist/timeZone/useArtistTimeZone';
import {
  initialStudioPublishChoice,
  studioPublishBlocksMake,
  studioPublishBody,
  type StudioPublishChoice,
} from './studioPublishChoice';

/** The id of the switch panel: "Make it" scrolls to it while a choice is missing. */
export const STUDIO_PUBLISH_FIELD_ID = 'studio-publish-youtube-field';

interface UseStudioPublishProps {
  accountId: number;
  catalogItemUuid: string;
  releaseDate: string | null;
  themeId: string | undefined;
}

/**
 * The Studio side of the one-off publish (owner decision 2026-10-09). With the switch on, each made
 * full-song video gets a waiting YouTube record at once (the server uploads it when the render
 * ends). With the switch off, the made video goes on the watch list: when it is ready, the
 * "Publish to YouTube?" toast shows (`PublishHost`).
 */
export function useStudioPublish({ accountId, catalogItemUuid, releaseDate, themeId }: UseStudioPublishProps) {
  const timeZone = useArtistTimeZone();
  const [choice, setChoice] = useState<StudioPublishChoice>(() =>
    initialStudioPublishChoice(releaseDate, new Date(), timeZone)
  );
  const [createPublish] = useCreateAssetPublishMutation();
  useEffect(
    () => setChoice(initialStudioPublishChoice(releaseDate, new Date(), timeZone)),
    [catalogItemUuid, releaseDate, timeZone]
  );

  const isFullVideo = isFullSongVideoTheme(themeId);
  const blocksMake = isFullVideo && studioPublishBlocksMake(choice, releaseDate, timeZone);

  // The switch means "publish this one": when another revision is live on YouTube, it replaces it
  // (owner decision 2026-10-09). The API names the live record in the 409.
  const publishWithReplace = useCallback(
    async (body: CreateAssetPublishBody) => {
      const result = await createPublish({ accountId, body });
      const cause =
        'error' in result ? (result.error as { m24Limit?: { code?: string; reason?: string } }).m24Limit : null;
      if (cause?.code === 'M24_PUBLISH_REPLACE_CHOICE_REQUIRED' && typeof cause.reason === 'string') {
        await createPublish({ accountId, body: { ...body, replacesPublishUuid: cause.reason } });
      }
    },
    [accountId, createPublish]
  );

  const afterMade = useCallback(
    (created: Array<{ uuid: string; themeId: string }>) => {
      const videos = created.filter((asset) => isFullSongVideoTheme(asset.themeId));
      const watched = videos.filter((asset) => {
        const body = studioPublishBody(choice, asset, releaseDate, timeZone);
        if (body === null) {
          return true;
        }
        publishWithReplace(body).catch(console.error);
        return false;
      });
      if (watched.length > 0) {
        const at = Date.now();
        addPublishWatches(
          watched.map((asset) => ({ accountId, kitAssetUuid: asset.uuid, catalogItemUuid, themeId: asset.themeId, at }))
        );
      }
    },
    [accountId, catalogItemUuid, choice, publishWithReplace, releaseDate, timeZone]
  );

  const showMissingChoice = useCallback(() => {
    document.getElementById(STUDIO_PUBLISH_FIELD_ID)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return { choice, setChoice, isFullVideo, blocksMake, afterMade, showMissingChoice };
}
